/**
 * Canonical row ordering for the timeline.
 *
 * Tasks are ordered by start date wherever dependencies allow, and by dependency
 * wherever dates tie or conflict.
 *
 * `startTime` is a date (day granularity) on the backend, so every task starting on
 * the same day is a tie. A same-day sequential checklist is nothing but ties, and the
 * precedence chain is the only record of its real order — sorting on the date alone
 * leaves those rows in an arbitrary order.
 *
 * Kahn's algorithm with a priority queue, mirroring `task-sequence.util.ts` on the
 * backend. Not a "dependency level" sort: levels are wrong globally, because an
 * independent task starting on day 30 would sort above a dependent task starting on
 * day 1.
 */

export interface OrderableTask {
  id: string;
  startMs: number;
  /**
   * Position in the parent/child tree flattened depth-first. It inherits the backend's
   * order, which is itself canonical and deterministic, so it is a stable final tiebreak.
   */
  originalIdx: number;
  presedentTaskId?: string[] | null;
}

/** Total order over tasks ready to be emitted: start date, then the backend's order. */
const compareTasks = (a: OrderableTask, b: OrderableTask): number =>
  a.startMs - b.startMs || a.originalIdx - b.originalIdx;

/** Returns the tasks in canonical order. Does not mutate the input. */
export function sortBySequence<T extends OrderableTask>(tasks: T[]): T[] {
  const byId = new Map<string, T>(tasks.map((task) => [task.id, task]));

  // precedent -> dependents, plus how many precedents each task still waits on.
  const dependents = new Map<string, string[]>();
  const pendingPrecedents = new Map<string, number>();

  tasks.forEach((task) => pendingPrecedents.set(task.id, 0));

  tasks.forEach((task) => {
    // A precedent that is absent (deleted, or outside this project) or self-referencing
    // is ignored, not treated as a dependency that can never be satisfied.
    const precedents = (task.presedentTaskId ?? []).filter(
      (pid) => pid !== task.id && byId.has(pid),
    );

    // Dedupe, so a precedent listed twice does not strand the task forever.
    Array.from(new Set(precedents)).forEach((pid) => {
      if (!dependents.has(pid)) dependents.set(pid, []);
      dependents.get(pid)!.push(task.id);
      pendingPrecedents.set(task.id, pendingPrecedents.get(task.id)! + 1);
    });
  });

  const ready = tasks
    .filter((task) => pendingPrecedents.get(task.id) === 0)
    .sort(compareTasks);
  const ordered: T[] = [];
  const emitted = new Set<string>();

  while (ready.length > 0) {
    const next = ready.shift()!;
    ordered.push(next);
    emitted.add(next.id);

    (dependents.get(next.id) ?? []).forEach((dependentId) => {
      const remaining = pendingPrecedents.get(dependentId)! - 1;
      pendingPrecedents.set(dependentId, remaining);
      if (remaining > 0) return;

      // Insert in sorted position rather than re-sorting the whole queue.
      const task = byId.get(dependentId)!;
      const at = ready.findIndex((candidate) => compareTasks(task, candidate) < 0);
      if (at === -1) ready.push(task);
      else ready.splice(at, 0, task);
    });
  }

  // Anything left is caught in a precedence cycle. Bad data must degrade, not vanish:
  // append those rows deterministically so they still render.
  const leftover = tasks
    .filter((task) => !emitted.has(task.id))
    .sort(compareTasks);

  return [...ordered, ...leftover];
}
